import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from "react";
import { Check } from "lucide-react";
import { createPortal } from "react-dom";

/**
 * Menu de ações — o menu contextual de linha, card e cabeçalho da V2.
 *
 * É um menu ARIA completo, não um bloco que aparece no hover: setas, Home, End,
 * Enter, Espaço, Escape, clique fora e retorno de foco ao gatilho
 * (AGENTS §14.3, §17). Um item desabilitado continua visível e explica o motivo,
 * em vez de sumir (§18).
 *
 * O painel sai por portal no `body` e é posicionado em coordenadas de viewport
 * a partir do gatilho. É o que impede o menu de linha de ser recortado pela
 * rolagem da tabela ou por qualquer ancestral com `overflow` (§14.3, §25.5).
 */
export interface MenuItem {
  id: string;
  label: string;
  icon?: ReactNode;
  /** Ação destrutiva: recebe a cor de perigo e vem depois de um separador. */
  danger?: boolean;
  disabled?: boolean;
  /** Motivo da indisponibilidade; vira `title` e texto acessível (§18). */
  disabledReason?: string;
  /** Marca o item como o estado corrente, em menus que escolhem um valor. */
  selected?: boolean;
  /**
   * Cor do ícone quando ele distingue categorias dentro do mesmo menu — é o
   * que o protótipo faz no menu de anexos. Sem isso, o ícone usa o acento.
   */
  tone?: string;
}

export interface MenuSection {
  id: string;
  label?: string;
  items: MenuItem[];
}

export interface MenuProps {
  /** Gatilho: recebe os atributos de expansão e a referência de foco. */
  trigger: (args: {
    ref: (node: HTMLButtonElement | null) => void;
    onClick: () => void;
    "aria-haspopup": "menu";
    "aria-expanded": boolean;
    "aria-controls": string | undefined;
  }) => ReactNode;
  sections: MenuSection[];
  onSelect: (id: string) => void;
  /** Nome acessível do menu. */
  label: string;
  /** Cabeçalho visível do painel: o nome do registro, em caixa normal. */
  title?: string;
  align?: "start" | "end";
  className?: string;
}

export function Menu({
  trigger,
  sections,
  onSelect,
  label,
  title,
  align = "end",
  className,
}: MenuProps) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const itemsRef = useRef<Array<HTMLButtonElement | null>>([]);
  const panelId = useId();
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  /**
   * Todos os itens entram na navegação, inclusive o indisponível.
   *
   * Item desabilitado por `disabled` some do foco, e com ele some a
   * explicação: quem usa teclado nunca ouviria o motivo. O padrão ARIA para
   * menu prevê item focável com `aria-disabled`, que é o que mantém o motivo
   * audível sem deixar a ação acontecer (AGENTS §17, §18).
   */
  const all = useMemo(() => sections.flatMap((section) => section.items), [sections]);

  const close = useCallback(
    (restoreFocus = true) => {
      setOpen(false);
      setActiveIndex(0);
      if (restoreFocus) triggerRef.current?.focus();
    },
    [],
  );

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      // Clicar fora fecha sem devolver o foco: o usuário já está em outro lugar.
      close(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [close, open]);

  /**
   * Coordenadas de viewport: o painel abre sob o gatilho, alinhado pela borda
   * que `align` indica, e é trazido para dentro da tela quando não couber.
   */
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const trigger = triggerRef.current;
      const panel = panelRef.current;
      if (!trigger) return;
      const anchor = trigger.getBoundingClientRect();
      const width = panel?.offsetWidth ?? 190;
      const height = panel?.offsetHeight ?? 0;
      const margin = 8;
      let left = align === "end" ? anchor.right - width : anchor.left;
      left = Math.min(Math.max(margin, left), Math.max(margin, window.innerWidth - width - margin));
      let top = anchor.bottom + 6;
      if (height && top + height > window.innerHeight - margin) {
        top = Math.max(margin, anchor.top - height - 6);
      }
      setPosition({ top, left });
    };
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [align, open, sections]);

  useEffect(() => {
    if (!open) setPosition(null);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    itemsRef.current[activeIndex]?.focus();
  }, [activeIndex, open]);

  const move = (step: number) => {
    if (all.length === 0) return;
    setActiveIndex((current) => (current + step + all.length) % all.length);
  };

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        move(1);
        break;
      case "ArrowUp":
        event.preventDefault();
        move(-1);
        break;
      case "Home":
        event.preventDefault();
        setActiveIndex(0);
        break;
      case "End":
        event.preventDefault();
        setActiveIndex(Math.max(0, all.length - 1));
        break;
      case "Escape":
        event.preventDefault();
        event.stopPropagation();
        close();
        break;
      case "Tab":
        // Tab sai do menu: fechar sem prender o foco evita a armadilha de um
        // popover não modal que se comporta como diálogo.
        close(false);
        break;
      default:
        break;
    }
  };

  let index = -1;

  return (
    <div ref={rootRef} className={className ? `cx-menu ${className}` : "cx-menu"}>
      {trigger({
        ref: (node) => {
          triggerRef.current = node;
        },
        onClick: () => {
          setActiveIndex(0);
          setOpen((value) => !value);
        },
        "aria-haspopup": "menu",
        "aria-expanded": open,
        "aria-controls": open ? panelId : undefined,
      })}
      {open &&
        createPortal(
        <div
          ref={panelRef}
          id={panelId}
          role="menu"
          aria-label={label}
          className={className ? `cx-menu-panel ${className}-panel` : "cx-menu-panel"}
          data-align={align}
          style={{ top: position?.top ?? -9999, left: position?.left ?? -9999 }}
          onKeyDown={handleKeyDown}
        >
          {title && <strong className="cx-menu-title">{title}</strong>}
          {sections.map((section, sectionIndex) => (
            <div key={section.id} className="cx-menu-section">
              {sectionIndex > 0 && <hr />}
              {section.label && <p className="cx-menu-label">{section.label}</p>}
              {section.items.map((item) => {
                const position = (index += 1);
                return (
                  <button
                    key={item.id}
                    ref={(node) => {
                      itemsRef.current[position] = node;
                    }}
                    type="button"
                    role="menuitem"
                    className="cx-menu-item"
                    data-danger={item.danger ? "true" : undefined}
                    data-selected={item.selected ? "true" : undefined}
                    aria-disabled={item.disabled ? "true" : undefined}
                    title={item.disabled ? item.disabledReason : undefined}
                    tabIndex={position === activeIndex ? 0 : -1}
                    onClick={() => {
                      if (item.disabled) return;
                      onSelect(item.id);
                      close();
                    }}
                  >
                    {item.icon && (
                      <span
                        aria-hidden="true"
                        style={item.tone ? ({ color: item.tone } as CSSProperties) : undefined}
                      >
                        {item.icon}
                      </span>
                    )}
                    <span>{item.label}</span>
                    {item.selected && <Check className="cx-menu-check" aria-hidden="true" />}
                    {item.disabled && item.disabledReason && (
                      <span className="cx-sr-only">{item.disabledReason}</span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}
